import React, { createContext, useState, useCallback, useEffect } from 'react';
import { applyUiPreferencesFromUser } from '../../../shared/utils/uiPreferences';

/**
 * AuthContext - Centralized authentication state management
 *
 * Provides:
 * - user: Complete user object from login
 * - token: JWT authentication token
 * - role: Current user's role (shortcut for user?.role)
 * - isAuthenticated: Boolean flag for auth status
 * - isLoading: Boolean flag for initialization loading state
 * - logout(): Method to clear auth state
 * - updateUserData(): Method to update user info (e.g., profile picture)
 * - setToken(): Method to set token after login
 * - setUser(): Method to set user after login
 */
export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize auth state from sessionStorage on mount
  useEffect(() => {
    const initializeAuth = () => {
      try {
        const storedToken = sessionStorage.getItem('token');
        const storedUser = sessionStorage.getItem('user');

        if (storedToken && storedUser) {
          const userData = JSON.parse(storedUser);

          // Validate token format (JWT should have 3 parts separated by dots)
          const isValidTokenFormat = storedToken.split('.').length === 3;

          if (isValidTokenFormat && userData && userData.id) {
            setToken(storedToken);
            setUser(userData);
            applyUiPreferencesFromUser(userData);
          } else {
            // Token format is invalid, clear it
            sessionStorage.removeItem('token');
            sessionStorage.removeItem('user');
          }
        }
      } catch (error) {
        console.error('Error initializing auth from sessionStorage:', error);
        // Clear corrupted data
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Logout - clear auth state and sessionStorage
  const logout = useCallback(async () => {
    // Clear state
    setUser(null);
    setToken(null);

    // Clear sessionStorage
    try {
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('location_id');
      sessionStorage.removeItem('location_name');
      sessionStorage.removeItem('organization_name');
      sessionStorage.removeItem('session_created_at');
      sessionStorage.removeItem('browser_mode');
      sessionStorage.clear();
    } catch (error) {
      console.warn('Error clearing sessionStorage:', error.message);
    }
  }, []);

  // Update user data (e.g., after profile picture update)
  const updateUserData = useCallback((updatedUserData) => {
    setUser(updatedUserData);
    sessionStorage.setItem('user', JSON.stringify(updatedUserData));
    applyUiPreferencesFromUser(updatedUserData);
  }, []);

  // Get the current role
  const role = user?.role || null;
  const isAuthenticated = !!(user && token);

  const value = {
    user,
    token,
    role,
    isAuthenticated,
    isLoading,
    setUser,
    setToken,
    logout,
    updateUserData,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
